import React, { FC, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";

import {
  PostBillAPIContext,
  PostBillContext,
  UppAuthContext,
} from "@app/provider";
import { HEAP_EVENTS } from "@app/provider/types";
import { routes } from "@app/routing";
import {
  PostBillErrors,
  PublicGuestValidationInput,
  PublicGuestValidationInputCaseNumber,
  PublicGuestValidationInputEmail,
  PublicGuestValidationInputMobilePhone,
  PublicGuestValidationInputZipCode,
} from "@app/postBill/models/apiControllerModel";
import { LoginMethod } from "@app/postBill/models/postBillHeap";
import { usePostBillHeapEvent } from "@app/postBill/hooks";
import { updatePostbillLocalStorage } from "@app/postBill/utils/localStorage";
import { HeapExtraData } from "@app/postBill/hooks/usePostBillHeapEvent";
import BillingContactUs from "@app/postBill/components/billingContactUs";
import BillingCenterGuestVerification from "@app/postBill/components/billingCenterGuestVerification";
import { VerificationType } from "@app/postBill/components/billingCenterGuestVerification/billingCenterGuestVerification";
import BillingFooter from "@app/components/footers/billingFooter";
import { Notification } from "@app/components";
import UPSDobVerificationFailed from "@app/ups/pages/private/upsDobVerificationFailed";

import "./billingCenterGuestLogin.scss";
import "@app/preBill/preBill.scss";

const messages = defineMessages({
  publicGuestLoginPageErrorIncorrectInformationTitle: {
    id: "publicGuestLoginPageErrorIncorrectInformationTitle",
    defaultMessage: "Incorrect information",
  },
  publicGuestLoginPageErrorIncorrectInformationContent: {
    id: "publicGuestLoginPageErrorIncorrectInformationContent",
    defaultMessage:
      "Information entered should match what is on file with your clinic.",
  },
  publicGuestLoginPageTitle: {
    id: "publicGuestLoginPageTitle",
    defaultMessage: "Review and pay your bill",
  },
  publicGuestLoginPageSubtitle: {
    id: "publicGuestLoginPageSubtitle",
    defaultMessage: "Enter your information to review your bill",
  },
  publicGuestLoginPageSubtitle2: {
    id: "publicGuestLoginPageSubtitle2",
    defaultMessage: "Enter additional information to review your bill",
  },
  publicGuestLoginInvalidDateOfBirth: {
    id: "publicGuestLoginInvalidDateOfBirth",
    defaultMessage: "Incorrect date of birth. Please re-enter",
  },
});

type NonCaseVerification = Exclude<
  PublicGuestValidationInput,
  PublicGuestValidationInputCaseNumber
>;

const BillingCenterGuestLogin: FC = () => {
  const intl = useIntl();
  const [showError, setShowError] = useState<boolean>(false);
  const ATTEMPTS = 3;
  const [attemptsLeft, setAttemptsLeft] = useState<number>(ATTEMPTS);
  const [verificationType, setVerificationType] = useState<VerificationType>(
    VerificationType.CASE_NUMBER
  );
  type ErrorType = "generic" | "dob";
  const [errorType, setErrorType] = useState<ErrorType>("generic");
  const { setGuestProfileUid } = useContext(UppAuthContext);
  const { setLoginMethod } = useContext(PostBillContext);

  const {
    apiProviderState: { loadingStates },
    publicLogin,
  } = useContext(PostBillAPIContext);
  const navigate = useNavigate();
  const { trackPostBillHeapEvent } = usePostBillHeapEvent();

  const prepareExtraDataForHeap = (data: PublicGuestValidationInput) => {
    const extraData: HeapExtraData = {
      public_login_method: verificationType,
      date_of_birth: `${data.year}/${data.month}/${data.day}`,
    };

    if (verificationType !== VerificationType.CASE_NUMBER) {
      extraData.first_name = (data as NonCaseVerification).firstName ?? "";
      extraData.last_name = (data as NonCaseVerification).lastName ?? "";
      if (verificationType === VerificationType.ZIPCODE) {
        extraData.zip_code =
          (data as PublicGuestValidationInputZipCode).zipCode ?? "";
      } else if (verificationType === VerificationType.MOBILE_NUMBER) {
        extraData.mobile_number =
          (data as PublicGuestValidationInputMobilePhone).mobilePhone ?? "";
      } else {
        extraData.email = (data as PublicGuestValidationInputEmail).email ?? "";
      }
    } else {
      extraData.case_number =
        (data as PublicGuestValidationInputCaseNumber).caseNumber ?? "";
    }
    return extraData;
  };

  const handlePublicGuestValidation = async (
    data: PublicGuestValidationInput
  ): Promise<void> => {
    setShowError(false);
    setErrorType("generic");

    await publicLogin(data)
      .then((response) => {
        const patientUid = response.data?.patient.uid as string;
        setGuestProfileUid(patientUid);
        trackPostBillHeapEvent({
          eventKey: HEAP_EVENTS.upp_public_billing_order_match_success,
          extraData: prepareExtraDataForHeap(data),
        });
        updatePostbillLocalStorage("authType", "public");
        setLoginMethod(LoginMethod.PUBLIC);
        navigate(routes.billingCenter);
      })
      .catch((error) => {
        trackPostBillHeapEvent({
          eventKey: HEAP_EVENTS.upp_public_billing_order_match_failure,
          extraData: prepareExtraDataForHeap(data),
        });
        const code = (error?.message || "").toString();
        if (
          code === PostBillErrors.PATIENT_DOB_MISMATCH ||
          code === PostBillErrors.INCORRECT_DOB
        ) {
          setErrorType("dob");
          setAttemptsLeft((prev) => {
            const next = prev - 1;
            if (next > 0) {
              setShowError(true);
            }
            return next;
          });
        } else {
          setErrorType("generic");
          setShowError(true);
        }
      });
  };

  const renderNotification = () => {
    const notificationMessage =
      errorType === "dob"
        ? messages.publicGuestLoginInvalidDateOfBirth
        : messages.publicGuestLoginPageErrorIncorrectInformationContent;
    return (
      <Notification
        className="billing-center-guest-login__notification"
        titleElem={intl.formatMessage(
          messages.publicGuestLoginPageErrorIncorrectInformationTitle
        )}
        type="error"
      >
        {intl.formatMessage(notificationMessage)}
      </Notification>
    );
  };

  if (attemptsLeft <= 0) {
    return (
      <UPSDobVerificationFailed
        onCreateAccount={() => navigate(routes.signUp)}
      />
    );
  }

  return (
    <div className="billing-center-guest-login billing-gradient">
      <div className="billing-center-guest-login__container">
        <section className="billing-guest-login-page__container__title">
          <h2>{intl.formatMessage(messages.publicGuestLoginPageTitle)}</h2>
          <span>
            {verificationType === VerificationType.CASE_NUMBER
              ? intl.formatMessage(messages.publicGuestLoginPageSubtitle)
              : intl.formatMessage(messages.publicGuestLoginPageSubtitle2)}
          </span>
        </section>
        {showError && renderNotification()}
        <BillingCenterGuestVerification
          loading={loadingStates.publicLogin}
          verificationType={verificationType}
          setVerificationType={setVerificationType}
          onSubmit={handlePublicGuestValidation}
        />
        <BillingContactUs />
        <BillingFooter />
      </div>
    </div>
  );
};

export default BillingCenterGuestLogin;
